import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/api/client";
import type { PaginationMeta } from "./types";

export function useFinanceQueue<T>(
  loadPage: (params: {
    cursor?: string;
    q: string;
    status: string;
    sort: string;
  }) => Promise<{ items: T[]; pagination: PaginationMeta }>,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const status = searchParams.get("status") ?? "";
  const sort = searchParams.get("sort") ?? "";
  const cursor = searchParams.get("cursor") ?? undefined;

  const [rows, setRows] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadPage({
        ...(cursor ? { cursor } : {}),
        q,
        status,
        sort,
      });
      setRows(result.items);
      setNextCursor(result.pagination.next_cursor);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRows([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [loadPage, cursor, q, status, sort]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  const page = cursorStack.length + 1;

  return {
    q,
    status,
    sort,
    rows,
    loading,
    error,
    searchDraft,
    setSearchDraft,
    nextCursor,
    page,
    load,
    applySearch() {
      setCursorStack([]);
      updateParams({ q: searchDraft.trim() || undefined, cursor: undefined });
    },
    setStatus(value: string) {
      setCursorStack([]);
      updateParams({ status: value || undefined, cursor: undefined });
    },
    setSort(value: string) {
      setCursorStack([]);
      updateParams({ sort: value, cursor: undefined });
    },
    goNext() {
      if (!nextCursor) return;
      setCursorStack((stack) => [...stack, cursor ?? ""]);
      updateParams({ cursor: nextCursor });
    },
    goPrevious() {
      if (cursorStack.length === 0) return;
      const stack = [...cursorStack];
      const previous = stack.pop();
      setCursorStack(stack);
      updateParams({
        cursor: previous && previous.length > 0 ? previous : undefined,
      });
    },
  };
}
